import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useApplicant } from "@/hooks/useApplicant";
import { useFormResponses } from "@/hooks/useFormResponses";
import { Field, Select, TextInput } from "@/components/Field";
import { cn } from "@/lib/utils";
import type { UpdatePersonRequest } from "@/lib/api-types";
import {
  getFieldErrors,
  getFormError,
  getFormErrorAlways,
  runUpserts,
} from "@/lib/validation-errors";

/**
 * Step 1.5 — Biographic information (Part 3 of N-400).
 *
 * Height, weight, eye color, hair color live on the Person row and are
 * sent via PATCH. Ethnicity and race live in form_responses (the data
 * model doesn't have columns for them; the form-mapping layer reads
 * the keys directly).
 *
 * USCIS uses these for biometrics matching at the interview, so they
 * need to be reasonably accurate but not surgical — applicants can
 * estimate.
 */

// form_responses keys for ethnicity + race. Names must match what
// the form mapping (n400_2025_01_20.go) reads.
const FR_KEYS = {
  ethnicity: "ethnicity",
  race: "race",
} as const;

// Eye-color options. Values match the strings the mapping's
// `eyeColorState` switch recognises.
const EYE_OPTIONS = [
  { value: "black", label: "Black" },
  { value: "blue", label: "Blue" },
  { value: "brown", label: "Brown" },
  { value: "green", label: "Green" },
  { value: "gray", label: "Gray" },
  { value: "hazel", label: "Hazel" },
  { value: "maroon", label: "Maroon" },
  { value: "pink", label: "Pink" },
  { value: "other", label: "Other" },
];

const HAIR_OPTIONS = [
  { value: "bald", label: "Bald" },
  { value: "black", label: "Black" },
  { value: "blond", label: "Blond" },
  { value: "brown", label: "Brown" },
  { value: "gray", label: "Gray" },
  { value: "red", label: "Red" },
  { value: "sandy", label: "Sandy" },
  { value: "white", label: "White" },
  { value: "other", label: "Other" },
];

// Race options — values match the mapping's `raceState` switch.
// Multi-select: applicants can pick more than one.
const RACE_OPTIONS = [
  { value: "white", label: "White" },
  { value: "black", label: "Black or African American" },
  { value: "asian", label: "Asian" },
  { value: "american_indian", label: "American Indian or Alaska Native" },
  { value: "native_hawaiian", label: "Native Hawaiian or Other Pacific Islander" },
];

export function StepBiographic() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { applicant, isLoading, update, error } = useApplicant(id);
  const fr = useFormResponses(id);

  const [heightCm, setHeightCm] = useState("");
  const [weightKg, setWeightKg] = useState("");
  const [eyeColor, setEyeColor] = useState("");
  const [hairColor, setHairColor] = useState("");
  const [ethnicity, setEthnicity] = useState("");
  const [races, setRaces] = useState<Set<string>>(new Set());

  // Hydrate Person fields once applicant loads.
  useEffect(() => {
    if (!applicant) return;
    setHeightCm(applicant.height_cm != null ? String(applicant.height_cm) : "");
    setWeightKg(applicant.weight_kg != null ? String(applicant.weight_kg) : "");
    setEyeColor(applicant.eye_color ?? "");
    setHairColor(applicant.hair_color ?? "");
  }, [applicant]);

  // Hydrate form_responses (ethnicity + race).
  useEffect(() => {
    if (fr.isLoading) return;
    setEthnicity(fr.getString(FR_KEYS.ethnicity));
    // Race is stored as comma-separated string OR JSON array — accept both.
    const raw = fr.get(FR_KEYS.race);
    if (Array.isArray(raw)) {
      setRaces(new Set(raw.map(String)));
    } else if (typeof raw === "string" && raw) {
      setRaces(new Set(raw.split(",").map((s) => s.trim()).filter(Boolean)));
    } else {
      setRaces(new Set());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fr.isLoading, fr.get(FR_KEYS.ethnicity), fr.get(FR_KEYS.race)]);

  const toggleRace = (value: string) => {
    setRaces((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  };

  const [saveErrors, setSaveErrors] = useState<unknown[]>([]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!applicant) return;
    setSaveErrors([]);

    // 1) Person fields — PATCH only the ones the user touched.
    const patch: UpdatePersonRequest = {};
    if (heightCm) (patch as Record<string, unknown>).height_cm = parseInt(heightCm, 10);
    if (weightKg) (patch as Record<string, unknown>).weight_kg = parseInt(weightKg, 10);
    if (eyeColor) patch.eye_color = eyeColor;
    if (hairColor) patch.hair_color = hairColor;
    if (Object.keys(patch).length > 0) {
      try {
        await update.mutateAsync(patch);
      } catch {
        // update.error carries the failure for per-field rendering.
        return;
      }
    }

    // 2) form_responses — ethnicity + race.
    const upserts: Promise<unknown>[] = [];
    if (ethnicity) {
      upserts.push(fr.upsert.mutateAsync({ fieldKey: FR_KEYS.ethnicity, value: ethnicity }));
    }
    if (races.size > 0) {
      // Comma-join to match the mapper's parseable shape (it accepts
      // either a comma-separated string or a JSON array).
      upserts.push(
        fr.upsert.mutateAsync({
          fieldKey: FR_KEYS.race,
          value: Array.from(races).join(","),
        }),
      );
    }
    const errors = await runUpserts(upserts);
    if (errors.length > 0) {
      setSaveErrors(errors);
      return;
    }

    navigate(`/cases/${id}/wizard/addresses`);
  };

  if (isLoading) return <div className="text-slate-500">Loading…</div>;
  if (error) {
    return (
      <div className="text-red-600 text-sm">
        Could not load your application: {(error as Error).message}
      </div>
    );
  }

  const fieldErrors = getFieldErrors(update.error);
  const personFormError = getFormError(update.error);
  // ethnicity/race form_response errors are collected locally — see
  // runUpserts in validation-errors.ts. getFormErrorAlways turns any
  // 422 (even fields-only) into a visible string for the banner, since
  // there's no per-input UI for the Select + checkboxes.
  const frFormError = saveErrors.length > 0
    ? saveErrors.map(getFormErrorAlways).filter(Boolean).join("; ") || null
    : null;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Biographic information</h1>
        <p className="text-sm text-slate-500 mt-1">
          USCIS uses these for biometrics matching at your interview.
          Estimates are fine — you don&apos;t need to be exact.
        </p>
      </header>

      <fieldset className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
        <legend className="font-medium text-slate-900 px-1">Physical description</legend>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field
            label="Height (cm)"
            required
            hint="Your height in centimeters. 170 cm ≈ 5'7&quot;."
            error={fieldErrors?.["height_cm"]?.message}
          >
            <TextInput
              type="number"
              min={50}
              max={250}
              value={heightCm}
              onChange={(e) => setHeightCm(e.target.value)}
            />
          </Field>
          <Field
            label="Weight (kg)"
            required
            hint="Your weight in kilograms. 70 kg ≈ 154 lb."
            error={fieldErrors?.["weight_kg"]?.message}
          >
            <TextInput
              type="number"
              min={20}
              max={300}
              value={weightKg}
              onChange={(e) => setWeightKg(e.target.value)}
            />
          </Field>
          <Field
            label="Eye color"
            required
            error={fieldErrors?.["eye_color"]?.message}
          >
            <Select value={eyeColor} onChange={(e) => setEyeColor(e.target.value)}>
              <option value="">Select…</option>
              {EYE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          </Field>
          <Field
            label="Hair color"
            required
            error={fieldErrors?.["hair_color"]?.message}
          >
            <Select value={hairColor} onChange={(e) => setHairColor(e.target.value)}>
              <option value="">Select…</option>
              {HAIR_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          </Field>
        </div>
      </fieldset>

      <fieldset className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
        <legend className="font-medium text-slate-900 px-1">Ethnicity & race</legend>
        <p className="text-sm text-slate-500">
          USCIS asks for ethnicity and race separately, following U.S.
          Census categories. Pick the ones that apply.
        </p>

        <Field label="Ethnicity" required>
          <Select value={ethnicity} onChange={(e) => setEthnicity(e.target.value)}>
            <option value="">Select…</option>
            <option value="hispanic">Hispanic or Latino</option>
            <option value="not_hispanic">Not Hispanic or Latino</option>
          </Select>
        </Field>

        <div>
          <p className="text-sm font-medium text-slate-700 mb-2">
            Race (select all that apply) <span className="text-red-500">*</span>
          </p>
          <div className="space-y-2">
            {RACE_OPTIONS.map((o) => {
              const checked = races.has(o.value);
              return (
                <label
                  key={o.value}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2 rounded-lg border cursor-pointer transition-colors",
                    checked
                      ? "bg-slate-900 text-white border-slate-900"
                      : "bg-white text-slate-700 border-slate-300 hover:border-slate-400",
                  )}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleRace(o.value)}
                    className="accent-white"
                  />
                  <span className="text-sm">{o.label}</span>
                </label>
              );
            })}
          </div>
        </div>
      </fieldset>

      {personFormError && (
        <p className="text-sm text-red-600">Could not save: {personFormError}</p>
      )}
      {frFormError && (
        <p className="text-sm text-red-600">Could not save: {frFormError}</p>
      )}
      {fieldErrors && (
        <p className="text-xs text-red-600">
          Some fields above need attention before continuing.
        </p>
      )}

      <div className="flex items-center justify-between pt-4 border-t border-slate-200">
        <Link
          to={`/cases/${id}/wizard/personal`}
          className="text-sm text-slate-600 hover:text-slate-900"
        >
          ← Personal info
        </Link>
        <button
          type="submit"
          disabled={update.isPending || fr.upsert.isPending}
          className="rounded-xl bg-slate-900 text-white px-5 py-2.5 text-sm font-medium hover:bg-slate-800 disabled:bg-slate-300"
        >
          {update.isPending || fr.upsert.isPending ? "Saving…" : "Save & continue"}
        </button>
      </div>
    </form>
  );
}
